import { TLocale } from '@/config/locales';
import apiRequest from '@/services/apiRequest';
import { getCoreApi } from '@/utility/getCoreApi';

import { IBooking, IOwnerSummary, IRentalSummary, IRenterSummary } from './types/booking/details';

export interface IConversation {
  all_statuses: string;
  archived: boolean;
  bookings: IBooking[];
  created: string;
  created_by_id: number;
  id: number;
  last_location_id: number;
  messages?: IMessage[];
  num_messages: number;
  owner_id: number;
  owner_object: number;
  owner_sms_proxy_id: string;
  owner_summary: IOwnerSummary;
  read: true;
  renter_id: number;
  renter_object: number;
  renter_sms_proxy_id: string;
  renter_summary: IRenterSummary;
  sms_proxy_closed: boolean;
  sms_proxy_session_id: string;
  time_to_first_action: number;
  updated: string;
}

export interface IMessage {
  conversation_id?: number;
  sender_id?: number;
  text: string;
  timestamp?: string;
  ref_rental_id?: number;
  receiver_id?: number;
  rental_summary?: IRentalSummary;
}

export const PAGE_SIZE = 8;
type TGetConversationsParams = {
  order?: string;
  limit?: number;
  meta?: boolean;
  has_messages?: boolean;
  offset?: number;
  booking_status?: string;
  archived?: boolean;
  locale?: TLocale;
};

export type TConversationsMeta = {
  all?: number;
  new?: number;
  upcoming?: number;
  completed?: number;
  archived?: number;
};

export const getConversation = async (id: number): Promise<IConversation> => {
  const url = `${getCoreApi()}/conversations/${id}`;
  const conversation = await apiRequest<IConversation>({ url });
  return conversation;
};

export const getConversations = async (
  page: number,
  status: string,
  locale: TLocale,
): Promise<[IConversation[], number, TConversationsMeta]> => {
  const params: TGetConversationsParams = {
    order: 'updated',
    limit: PAGE_SIZE,
    meta: true,
    has_messages: true,
    offset: (page - 1) * PAGE_SIZE,
    locale,
  };

  switch (status) {
    case 'upcoming':
      params['booking_status'] = 'approved,handed_off,imminent';
      break;
    case 'new':
      params['booking_status'] = 'negotiating,conversation,draft';
      break;
    case 'completed':
      params['booking_status'] = 'returned';
      break;
    case 'archived':
      params['archived'] = true;
      break;
    default:
      break;
  }

  const url = `${getCoreApi()}/conversations`;
  const response = await apiRequest<IConversation[]>({ url, params }, false, true);

  if (!response) {
    return [[], 0, {}];
  }

  let meta: TConversationsMeta = {};
  const metaHeader = response.headers['meta'];
  if (metaHeader && typeof metaHeader === 'string') {
    try {
      meta = JSON.parse(metaHeader);
    } catch {
      meta = {};
    }
  }

  return [response.data, Number(response.headers['total-results']), meta];
};

export const postMessage = async (data: IMessage): Promise<IMessage> => {
  const url = `${getCoreApi()}/messages`;
  const message = await apiRequest<IMessage>({ url, method: 'POST', data }, true);
  return message;
};

export const archiveConversation = async (id: number): Promise<void> => {
  const url = `${getCoreApi()}/conversations/${id}`;
  await apiRequest({ url, method: 'DELETE' });
};

export const getPendingBookings = async (
  ownerId: number,
  renterId: number,
  locale: string,
): Promise<IBooking[]> => {
  const url = `${getCoreApi()}/bookings?locale=${locale}&owner_id=${ownerId}&renter_id=${renterId}&status=draft,negotiating,approved,imminent,handed_off`;
  const bookings = await apiRequest<IBooking[]>({ url });
  return bookings;
};

export const patchMessage = async (id: number, data: any): Promise<IMessage> => {
  const url = `${getCoreApi()}/conversations/${id}`;
  const message = await apiRequest<IMessage>({ url, method: 'PATCH', data }, true);
  return message;
};
